/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IconButton } from '@fluentui/react/lib/Button';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocation } from 'react-router';
import { CluEntityCreationModal } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntityCreationModal';
import { CLuTagUtterancesPageSidePane } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { CluDataDistribution } from 'src/clu/appShell/cluTagUtterancesPage/components/CluDataDistribution';
import { UtterancesGenerationWithGptPane } from 'src/clu/appShell/cluTagUtterancesPage/components/UtterancesGenerationWithGptPane';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityCapability, EntityStore } from 'src/core/types/EntityStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';
import { EntityDto, EntityPalette, PalettePivotType } from 'src/verticals/EntityPalette';
import { DisplayedFilters, FiltersBlade } from 'src/verticals/utterancesFilters/FiltersBlade';
import { UtteranceFiltersStore } from 'src/verticals/utterancesFilters/stores/UtteranceFiltersStore';
import { SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';
import styled from 'styled-components';

const messages = defineMessages({
    list: { id: 'clu.cluTagSidePaneContainer.list', defaultMessage: 'List' },
    tagging: { id: 'clu.cluTagSidePaneContainer.tagging', defaultMessage: 'Activity pane' },
    filters: { id: 'clu.cluTagSidePaneContainer.filters', defaultMessage: 'Filters' },
    learned: { id: 'clu.cluTagSidePaneContainer.learned', defaultMessage: 'Learned' },
    prebuilt: { id: 'clu.cluTagSidePaneContainer.prebuilt', defaultMessage: 'Prebuilt' },
    trainingSetDescription: {
        id: 'clu.cluTagSidePaneContainer.trainingSetDescription',
        defaultMessage:
            'Make sure to label any entities in your utterances that won’t get recognized by the entity’s prebuilt or list components.'
    },
    testingSetDescription: {
        id: 'clu.cluTagSidePaneContainer.testingSetDescription',
        defaultMessage:
            'Make sure to label all entities in your testing utterances, even those that would be recognized by the entity’s prebuilt or list components.'
    },
    closeFiltersPaneButtonAriaLabel: {
        id: 'clu.cluTagSidePaneContainer.closeFiltersPaneButtonAriaLabel',
        defaultMessage: 'Close filters'
    }
});

export type TagUtterancesPageSidePaneContainerProps = {
    onPaneClose: () => void;
    selectedEntityId: string;
    datasetType: DatasetType;
    labelingStore: LabelingStore;
    utterances: CluUtteranceSwagger[];
    filtersStore: UtteranceFiltersStore;
    onEditEntity: (entityId: string) => void;
    onAddEntity: (entityName: string) => void;
    activeSidePane: CLuTagUtterancesPageSidePane;
    setSelectedEntityId: (entityId: string) => void;
    onFiltersChange: (change: SelectionChange) => void;
};

const scopedTheme = getScopedTheme('TagUtterancesPageSidePaneContainer');

const DistributionPane = styled(Stack)({
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    paddingBottom: '10px',
    borderLeft: scopedTheme.borderSpecs
});

const filtersContainerStackStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: {
        height: '100%',
        marginBottom: '20px',
        borderLeft: scopedTheme.borderSpecs
    }
});

/**
 * Represents a container for the utterances tagging page filters and labeling side panes.
 */
export const CluTagSidePaneContainer = observer((props: TagUtterancesPageSidePaneContainerProps) => {
    const {
        utterances,
        onAddEntity,
        datasetType,
        onPaneClose,
        onEditEntity,
        filtersStore,
        labelingStore,
        activeSidePane,
        onFiltersChange,
        selectedEntityId,
        setSelectedEntityId
    } = props;
    const location = useLocation();
    const { localize } = useLocalization();
    const { rootStore, dispatcher, uiStore } = useGlobalState();

    const utterancesCount = utterances.length;
    const currentProject = rootStore.currentCluProjectStore;
    const intents = currentProject.intentStoreHouse.items;
    const entities = currentProject.entityStoreHouse.items;
    const entityLabelsCount = currentProject.classifiedLabelsPerEntity(datasetType);
    const displayedFilters: DisplayedFilters = currentProject.projectType === 'Orchestration' ? ['intents'] : ['entities', 'intents'];

    const cluPaletteIdRef = React.useRef<string>(getId());
    const addEntityButtonIdRef = React.useRef<string>(getId());

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CluAddEntity');
            dequeueTeachingBubble('CluDataTaggingPalette');
        },
        []
    );

    React.useEffect(() => {
        if (activeSidePane === 'entityPalette' && currentProject.projectType === 'Orchestration') {
            return;
        }

        if (
            uiStore.teachingBubbles.CluNewUtterance &&
            !uiStore.teachingBubbles.CluDataTaggingPalette &&
            !uiStore.skippedTeachingBubbles.CluNewUtterance
        ) {
            createTeachingBubble(
                'CluDataTaggingPalette',
                localize(cluTeachingBubbleMessages.dataTaggingPaletteBody),
                localize(cluTeachingBubbleMessages.dataTaggingPaletteTitle),
                `#${cluPaletteIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            uiStore.teachingBubbles.CluDataTaggingPalette &&
            !uiStore.teachingBubbles.CluAddEntity &&
            !uiStore.skippedTeachingBubbles.CluDataTaggingPalette
        ) {
            createTeachingBubble(
                'CluAddEntity',
                localize(cluTeachingBubbleMessages.addEntityButtonBody),
                localize(cluTeachingBubbleMessages.addEntityButtonTitle),
                `#${addEntityButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [activeSidePane, currentProject.projectType, Object.values(uiStore.teachingBubbles)]);

    const entityPaletteHeader = (
        <Stack styles={{ root: { paddingLeft: 12, paddingTop: 12, marginBottom: 10 } }} tokens={{ childrenGap: 8 }}>
            <LuisText as="h2" block variant="xLarge">
                {localize(messages.tagging)}
            </LuisText>
            <LuisText variant="smallPlus">
                {localize(datasetType === 'Train' ? messages.trainingSetDescription : messages.testingSetDescription)}
            </LuisText>
        </Stack>
    );

    const filtersHeader = (
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <LuisText variant="xLargePlus">{localize(messages.filters)}</LuisText>
            <IconButton
                autoFocus
                onClick={onPaneClose}
                iconProps={{ iconName: 'ChromeClose' }}
                ariaLabel={localize(messages.closeFiltersPaneButtonAriaLabel)}
                styles={{ icon: { color: scopedTheme.iconColor, fontSize: '12px' } }}
            />
        </Stack>
    );

    const handleEntityDeletion = React.useCallback(
        (entityId: string) =>
            dispatcher.dispatch('deleteCluEntities', { entityNames: [entities.find(e => e.id === entityId).name], toastData: {} }),
        [dispatcher.dispatch, entities]
    );

    const handleEntityUnSelection = React.useCallback(() => {
        setSelectedEntityId('');
        labelingStore.setLabelingMode('menu');
    }, [setSelectedEntityId, labelingStore]);

    const handleEntitySelection = React.useCallback(
        (entityId: string) => {
            setSelectedEntityId(entityId);
            labelingStore.setLabelingMode('brush');
            labelingStore.brushStore.setCurrentUtteranceId(entityId);
            labelingStore.brushStore.setIsValidState(true);
        },
        [setSelectedEntityId, labelingStore]
    );

    const getCluCapabilityText = (capability: EntityCapability) => {
        switch (capability) {
            case 'learned':
                return localize(messages.learned);
            case 'list':
                return localize(messages.list);
            case 'prebuilt':
                return localize(messages.prebuilt);
            default:
                return '';
        }
    };

    const getEntityDetailsText = (entity: EntityDto) => {
        let detailsText = '';
        const capabilities = (entity as EntityStore).capabilities(currentProject.labelsPerEntity.get(entity.name));
        const lastItemIndex = capabilities.length - 1;

        capabilities.forEach((capability, index) => {
            detailsText += `${getCluCapabilityText(capability)}${index === lastItemIndex ? '' : ','}`;
        });

        return detailsText;
    };

    return activeSidePane === 'entityPalette' ? (
        <EntityPalette
            isLoading={false}
            entities={entities}
            onAddEntity={onAddEntity}
            areEntityDetailsDisplayed
            projectId={currentProject.id}
            onEditEntity={onEditEntity}
            id={cluPaletteIdRef.current}
            labelsCount={entityLabelsCount}
            headerElement={entityPaletteHeader}
            selectedEntityId={selectedEntityId}
            cluProjectName={currentProject.name}
            onDeleteEntity={handleEntityDeletion}
            onSelectEntity={handleEntitySelection}
            onUnselectEntity={handleEntityUnSelection}
            getEntityDetailsText={getEntityDetailsText}
            addEntityButtonId={addEntityButtonIdRef.current}
            maxEntityNameCharLength={LuisConstants.Clu.maxNamesCharLength}
            openPivot={typeof location.state === 'string' ? (location.state as PalettePivotType) : null}
            onRenderAddEntityModal={rendererProps => <CluEntityCreationModal {...rendererProps} createLocally />}
            onRenderDistributionTab={() => (
                <CluDataDistribution
                    intents={intents}
                    entities={entities}
                    utterances={utterances}
                    projectType={currentProject.projectType}
                />
            )}
        />
    ) : activeSidePane === 'orchestrationDistribution' ? (
        <DistributionPane>
            <CluDataDistribution projectType={currentProject.projectType} utterances={utterances} intents={intents} entities={entities} />
        </DistributionPane>
    ) : activeSidePane === 'utterancesGptGeneration' ? (
        <Stack grow styles={filtersContainerStackStyles}>
            <UtterancesGenerationWithGptPane onCloseClicked={onPaneClose} />
        </Stack>
    ) : (
        <Stack grow styles={filtersContainerStackStyles}>
            <FiltersBlade
                displayedFilters={displayedFilters}
                filtersStore={filtersStore}
                onSelectionChange={onFiltersChange}
                utterancesCount={utterancesCount}
                headerElement={filtersHeader}
            />
        </Stack>
    );
});
